import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI } from '../api/api';
import AdBanner from '../components/AdBanner';
import { useWorkerLang } from '../contexts/WorkerLanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import SaveJobButton from '../components/SaveJobButton';
import JobMapView from '../components/JobMapView';
import JobsForYou from '../components/JobsForYou';
import SimpleJobCard from '../components/SimpleJobCard';
import PullToRefresh from '../components/PullToRefresh';
import { JobListSkeleton } from '../components/Skeletons';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../components/ui/drawer';
import { useIsMobile } from '../hooks/use-mobile';
import { haptic } from '../utils/haptics';
import { 
  MagnifyingGlass, 
  Funnel, 
  MapPin, 
  List, 
  Sparkle, 
  Heart, 
  X, 
  SlidersHorizontal,
  CurrencyDollar,
  Clock,
  Briefcase,
  CaretDown,
  CaretUp
} from '@phosphor-icons/react';

const BrowseJobs = () => {
  const navigate = useNavigate();
  const { t, lang } = useWorkerLang();
  const L = (en, es) => (lang === 'es' ? es : en);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState('list'); // 'list', 'map', or 'recommended'
  const [isWorker, setIsWorker] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  
  // Check if user is a worker
  useEffect(() => {
    const userType = localStorage.getItem('userType');
    setIsWorker(userType === 'worker');
    setIsLoggedIn(!!localStorage.getItem('token'));
  }, []);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    location: '',
    skills: '',
    budgetMin: 0,
    budgetMax: 50000,
    duration: '',
    sortBy: 'newest'
  });
  
  // Active filters for display
  const [activeFilters, setActiveFilters] = useState([]);

  const skillOptions = [
    'Electrical', 'Plumbing', 'Carpentry', 'HVAC', 'Roofing', 
    'Painting', 'Masonry', 'Drywall', 'Flooring', 'Welding'
  ];

  const durationOptions = [
    { value: '', label: L('Any Duration', 'Cualquier Duración') },
    { value: '1 week', label: L('1 Week', '1 Semana') },
    { value: '2 weeks', label: L('2 Weeks', '2 Semanas') },
    { value: '1 month', label: L('1 Month', '1 Mes') },
    { value: '2-3 months', label: L('2-3 Months', '2-3 Meses') },
    { value: '3+ months', label: L('3+ Months', '3+ Meses') }
  ];

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 10,
        sort_by: filters.sortBy
      };
      
      if (filters.search) params.search = filters.search;
      if (filters.location) params.location = filters.location;
      if (filters.skills) params.skills = filters.skills;
      if (filters.budgetMin > 0) params.budget_min = filters.budgetMin;
      if (filters.budgetMax < 50000) params.budget_max = filters.budgetMax;
      if (filters.duration) params.duration = filters.duration;
      
      const response = await jobsAPI.getAll(params);
      setJobs(response.data.jobs);
      setTotal(response.data.total);
      
      // Update active filters
      const active = [];
      if (filters.search) active.push({ key: 'search', label: `${L('Search', 'Búsqueda')}: ${filters.search}` });
      if (filters.location) active.push({ key: 'location', label: `${L('Location', 'Ubicación')}: ${filters.location}` });
      if (filters.skills) active.push({ key: 'skills', label: `${L('Skill', 'Habilidad')}: ${filters.skills}` });
      if (filters.budgetMin > 0 || filters.budgetMax < 50000) {
        active.push({ key: 'budget', label: `${L('Budget', 'Presupuesto')}: $${filters.budgetMin.toLocaleString()} - $${filters.budgetMax.toLocaleString()}` });
      }
      if (filters.duration) active.push({ key: 'duration', label: `${L('Duration', 'Duración')}: ${filters.duration}` });
      setActiveFilters(active);
      
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, page, lang]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchJobs();
  };

  const clearFilter = (key) => {
    if (key === 'budget') {
      setFilters(prev => ({ ...prev, budgetMin: 0, budgetMax: 50000 }));
    } else {
      setFilters(prev => ({ ...prev, [key]: '' }));
    }
  };

  const clearAllFilters = () => {
    setFilters({
      search: '',
      location: '',
      skills: '',
      budgetMin: 0,
      budgetMax: 50000,
      duration: '',
      sortBy: 'newest'
    });
  };

  // Handle search within map bounds
  const handleSearchArea = useCallback((bounds) => {
    // For now, we'll just trigger a refresh
    // In a full implementation, you could filter jobs by lat/lng bounds
    console.log('Search area bounds:', bounds);
    fetchJobs();
  }, [fetchJobs]);

  return (
    <PullToRefresh onRefresh={fetchJobs}>
    <div className="min-h-screen bg-gray-100 dark:bg-neutral-900 pb-safe transition-colors duration-300" data-testid="browse-jobs-page">
      <AdBanner placement="jobs" />
      {/* Logged-out visitor: join CTA */}
      {!isLoggedIn && (
        <div className="bg-[#D95800] border-b-4 border-black" data-testid="join-worker-banner">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <p className="text-white font-bold text-sm">
              {L('Want to apply to these jobs? Join free as a worker — takes 3 minutes.', '¿Quieres postularte a estos trabajos? Únete gratis como trabajador — toma 3 minutos.')}
            </p>
            <button
              onClick={() => navigate('/join-as-worker')}
              className="shrink-0 px-4 py-1.5 bg-black text-white text-xs font-black uppercase tracking-wider border-2 border-white shadow-[2px_2px_0px_#fff] hover:-translate-y-0.5 transition-transform"
              data-testid="join-worker-banner-cta"
            >
              {L('Join Free', 'Únete Gratis')}
            </button>
          </div>
        </div>
      )}
      {/* Simplified Header for Workers */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-black mb-2">{t('find_jobs')}</h1>
          <p className="text-lg text-blue-100">{total} {t('jobs_found')}</p>
          
          {/* Simple View Toggle */}
          <div className="flex gap-2 mt-4 flex-wrap">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl font-bold text-lg border-2 border-black dark:border-neutral-600 transition-all ${
                viewMode === 'list' 
                  ? 'bg-white dark:bg-neutral-800 text-gray-900 dark:text-white shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F]' 
                  : 'bg-transparent text-white hover:bg-white/20'
              }`}
              data-testid="view-mode-list"
            >
              <List size={24} weight="bold" />
              {L('All Jobs', 'Todos los Trabajos')}
            </button>
            {isWorker && (
              <button
                onClick={() => setViewMode('recommended')}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl font-bold text-lg border-2 border-black dark:border-neutral-600 transition-all ${
                  viewMode === 'recommended' 
                    ? 'bg-white dark:bg-neutral-800 text-gray-900 dark:text-white shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F]' 
                    : 'bg-transparent text-white hover:bg-white/20'
                }`}
                data-testid="view-mode-recommended"
              >
                <Sparkle size={24} weight="fill" />
                {L('For You', 'Para Ti')}
              </button>
            )}
            <button
              onClick={() => setViewMode('map')}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl font-bold text-lg border-2 border-black dark:border-neutral-600 transition-all ${
                viewMode === 'map' 
                  ? 'bg-white dark:bg-neutral-800 text-gray-900 dark:text-white shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F]' 
                  : 'bg-transparent text-white hover:bg-white/20'
              }`}
              data-testid="view-mode-map"
            >
              <MapPin size={24} weight="bold" />
              {t('map_view')}
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar - Always Visible */}
      <div className="bg-white dark:bg-neutral-800 border-b-4 border-black dark:border-neutral-700 py-4 sticky top-0 z-40 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlass size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t('search_placeholder')}
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full h-14 pl-12 pr-4 text-lg border-2 border-black rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2B4C6F] font-medium"
                data-testid="search-input"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-5 h-14 rounded-xl font-bold border-2 border-black transition-all ${
                showFilters 
                  ? 'bg-gray-900 text-white shadow-[3px_3px_0px_#2B4C6F]' 
                  : 'bg-white text-gray-900 shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000]'
              }`}
              data-testid="filter-toggle"
            >
              <Funnel size={24} weight="bold" />
              <span className="hidden sm:inline">{t('filters')}</span>
            </button>
          </div>

          {/* Simple Filters (desktop inline) */}
          {showFilters && !isMobile && (
            <div className="mt-4 p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">{t('location')}</label>
                  <input
                    type="text"
                    placeholder={t('any_location')}
                    value={filters.location}
                    onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#2B4C6F] font-medium"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">{t('skills')}</label>
                  <input
                    type="text"
                    placeholder={L('Plumbing, electrical...', 'Plomería, electricidad...')}
                    value={filters.skills}
                    onChange={(e) => setFilters(prev => ({ ...prev, skills: e.target.value }))}
                    className="w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#2B4C6F] font-medium"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">{t('budget_min')}</label>
                  <select
                    value={filters.budgetMin}
                    onChange={(e) => setFilters(prev => ({ ...prev, budgetMin: Number(e.target.value) }))}
                    className="w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#2B4C6F] font-medium bg-white"
                  >
                    <option value={0}>{L('Any', 'Cualquiera')}</option>
                    <option value={500}>$500+</option>
                    <option value={1000}>$1,000+</option>
                    <option value={2500}>$2,500+</option>
                    <option value={5000}>$5,000+</option>
                    <option value={10000}>$10,000+</option>
                  </select>
                </div>
              </div>
              <button
                onClick={clearAllFilters}
                className="mt-4 px-4 py-2 text-sm font-bold text-gray-600 hover:text-gray-900"
              >
                {t('clear_filters')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter bottom sheet */}
      <Drawer open={showFilters && isMobile} onOpenChange={setShowFilters}>
        <DrawerContent className="border-t-4 border-black" data-testid="filters-bottom-sheet">
          <DrawerHeader className="text-left pb-2">
            <DrawerTitle className="font-black uppercase tracking-tight">{t('filters')}</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-8 space-y-4 overflow-y-auto">
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">{t('location')}</label>
              <input
                type="text"
                placeholder={t('any_location')}
                value={filters.location}
                onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                className="w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#2B4C6F] font-medium"
                data-testid="sheet-location-input"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">{t('skills')}</label>
              <input
                type="text"
                placeholder={L('Plumbing, electrical...', 'Plomería, electricidad...')}
                value={filters.skills}
                onChange={(e) => setFilters(prev => ({ ...prev, skills: e.target.value }))}
                className="w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#2B4C6F] font-medium"
                data-testid="sheet-skills-input"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">{t('budget_min')}</label>
              <select
                value={filters.budgetMin}
                onChange={(e) => setFilters(prev => ({ ...prev, budgetMin: Number(e.target.value) }))}
                className="w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#2B4C6F] font-medium bg-white"
                data-testid="sheet-budget-select"
              >
                <option value={0}>{L('Any', 'Cualquiera')}</option>
                <option value={500}>$500+</option>
                <option value={1000}>$1,000+</option>
                <option value={2500}>$2,500+</option>
                <option value={5000}>$5,000+</option>
                <option value={10000}>$10,000+</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => { haptic(12); setShowFilters(false); }}
                className="flex-1 h-14 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                data-testid="sheet-apply-filters-btn"
              >
                {L('Show Results', 'Ver Resultados')}
              </button>
              <button
                onClick={() => { haptic(8); clearAllFilters(); }}
                className="px-5 h-14 bg-white text-gray-700 font-bold rounded-xl border-2 border-gray-300 active:translate-y-1 transition-all"
                data-testid="sheet-clear-filters-btn"
              >
                {t('clear_filters')}
              </button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Active Filters Display */}
      {activeFilters.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 mt-4">
          <div className="flex flex-wrap gap-2" data-testid="active-filters">
            {activeFilters.map(filter => (
              <Badge 
                key={filter.key} 
                variant="secondary" 
                className="bg-orange-100 text-orange-800 px-3 py-2 flex items-center gap-2 font-bold border-2 border-orange-300 rounded-lg"
              >
                {filter.label}
                <button onClick={() => clearFilter(filter.key)} className="hover:text-orange-600">
                  <X size={16} weight="bold" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6 flex justify-between items-center">
          <p className="text-gray-600">
            {loading ? t('loading') : (
              <>{L('Showing', 'Mostrando')} <span className="font-semibold">{jobs.length}</span> {L('of', 'de')} <span className="font-semibold">{total}</span> {t('jobs_found')}</>
            )}
          </p>
          {viewMode === 'map' && !loading && jobs.length > 0 && (
            <p className="text-sm text-gray-500">
              {L('Click markers on the map to see job details', 'Haz clic en los marcadores del mapa para ver los detalles')}
            </p>
          )}
        </div>

        {/* Map View */}
        {viewMode === 'map' && (
          <JobMapView jobs={jobs} loading={loading} onSearchArea={handleSearchArea} />
        )}

        {/* Recommended View (Jobs For You) */}
        {viewMode === 'recommended' && (
          <JobsForYou compact={false} />
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <>
            {loading ? (
              <JobListSkeleton count={5} />
            ) : jobs.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-md">
                <Briefcase className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('no_results')}</h3>
                <p className="text-gray-500 dark:text-gray-400">{t('try_clear')}</p>
                <Button onClick={clearAllFilters} className="mt-4" variant="outline">
                  {t('clear_filters')}
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="jobs-list">
                {jobs.map((job) => (
                  <SimpleJobCard key={job._id} job={job} />
                ))}
              </div>
            )}

            {/* Pagination - only show in list view */}
            {total > 10 && (
              <div className="mt-12 flex justify-center gap-2">
                <Button 
                  variant="outline" 
                  disabled={page === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  {L('Previous', 'Anterior')}
                </Button>
                <span className="px-4 py-2 text-gray-600">
                  {L('Page', 'Página')} {page} {L('of', 'de')} {Math.ceil(total / 10)}
                </span>
                <Button 
                  variant="outline" 
                  disabled={page >= Math.ceil(total / 10)}
                  onClick={() => setPage(p => p + 1)}
                >
                  {L('Next', 'Siguiente')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </PullToRefresh>
  );
};

export default BrowseJobs;
